import { ADT } from "pb.adt";
import { safe, unwrap } from "pb.safe";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
	POSConnectorClient,
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
} from "../../../../@lib-pos-connector-client";
import { logger } from "../../../../shared/logger";
import { never } from "../../../../shared/never";
import * as transport from "../../../../transport";
import { checkInternetConnection } from "../../shared/check-internet-connection";
import { DeclineResponse_fromRefundPosConnectorResponse } from "../../shared/declined-response";
import { getPageProps } from "../../shared/get-declined-page-props";
import { isUuid } from "../../shared/is-uuid";
import { ProtocolTransaction_fromPosConnectorResponse } from "../../shared/protocol-transaction-from-pos-connector-response";
import { useApiClient } from "../../shared/use-api-client";
import { useInactivityTimeout } from "../../shared/use-inactivity-timeout";
import type { RefundMatchedRequest } from "../../terminal-refund";
import {
	RefundAcknowledgedSchema,
	RefundResponseSchema,
	RefundSignatureVerificationSchema,
	RefundTerminalBusySchema,
	RefundTransactionEventSchema,
} from "../shared/schemas";
import { getTransaction } from "./get-transaction";
import { getTransactions } from "./get-transactions";
import {
	ResponseStates,
	STATE_INITIAL_TIMEOUT,
	STATE_TRANSACTION_STATE_TIMEOUT,
	State,
	TransactionStates,
} from "./state";

const resolve = transport.Resolve<"Terminal_refund">();

export namespace usePosConnectorState {
	export type Props = {
		request: RefundMatchedRequest;
	};
}

export function usePosConnectorState({
	request: {
		query: {
			amount,
			amountAdjustable,
			purchase,
			reference,
			sessionUuid,
			terminalContact,
			terminalReceipt,
			terminalNote,
			terminalReceiptPrint,
		},
		headers: { deviceId = "" },
		tokens,
	},
}: usePosConnectorState.Props): State | Error {
	const [state, setState] = useState<State | Error>(State.Initialising());

	const apiClient = useApiClient();
	const clientRef = useRef<POSConnectorClient | undefined>(undefined);

	const clientSessionUuid = useMemo(
		() => sessionUuid ?? uuidv4(),
		[sessionUuid],
	);

	const sendResponseAcknowledgedEvent = useCallback(() => {
		clientRef.current?.send({
			action: "refund",
			eventId: uuidv4(),
			timestamp: Date.now().toString(),
			data: {
				state: "ACKNOWLEDGED",
				sessionUuid: clientSessionUuid,
			},
		});
	}, [clientSessionUuid]);

	const sendSignatureVerificationEvent = useCallback(
		(isValid: boolean) => {
			clientRef.current?.send({
				action: "signatureVerification",
				eventId: uuidv4(),
				timestamp: Date.now().toString(),
				data: {
					event: "SIGNATURE_VERIFICATION",
					isValid,
					sessionUuid: clientSessionUuid,
				},
			});
		},
		[clientSessionUuid],
	);

	const closeWithCancelled = useCallback(() => {
		clientRef.current?.send({
			action: "transactionEvent",
			eventId: uuidv4(),
			timestamp: Date.now().toString(),
			data: {
				sessionUuid: clientSessionUuid,
				event: "CANCEL",
			},
		});

		return void resolve.Error("Cancelled");
	}, [clientSessionUuid]);

	const closeWithConnectionFailure = useCallback(() => {
		logger.debug("refund: closing with connection failure");
		return void resolve.Error("Connection Failure");
	}, []);

	const closeWithInactivityTimeout = useCallback(() => {
		return void resolve.Error("Inactivity Timeout");
	}, []);

	const closeWithNetworkFailure = useCallback(() => {
		return void resolve.Error("Network Failure");
	}, []);

	const closeWithNotAvailable = useCallback(() => {
		logger.debug("refund: closing with paired device not available");
		return void resolve.Error("Paired Device Not Available");
	}, []);

	const closeWithPairedDeviceBusy = useCallback(() => {
		return void resolve.Error("Paired Device Busy");
	}, []);

	const closeWithTransactionNotFound = useCallback(() => {
		logger.debug("refund: closing with transaction not found");
		return void resolve.Error("Transaction Not Found");
	}, []);

	const retryRefund = useCallback(() => {
		if (!(state instanceof Error) && ADT.match(state, "AwaitClientRefund")) {
			setState(State.AwaitClientRefund({ ...state }));
		}
	}, [state]);

	const nextState = useCallback(
		(next: State | Error) => {
			setState((prev) => {
				logger.debug("refund: navigation", {
					prev:
						prev instanceof Error
							? {
									message: prev.message,
									name: prev.name,
								}
							: { $type: prev.$type },
					next:
						next instanceof Error
							? {
									message: next.message,
									name: next.name,
								}
							: { $type: next.$type },
				});

				if (prev instanceof Error) {
					return next;
				}

				if (next instanceof Error) {
					return next;
				}

				if (
					ADT.match(prev, "Initialising") ||
					ADT.match(prev, TransactionStates)
				) {
					return next;
				}

				// user selected a transaction from a list of transactions to
				// refund
				if (
					ADT.match(prev, "SelectTransaction") &&
					ADT.match(next, "RefundTransaction")
				) {
					return next;
				}

				// user returning to the list of transactions after selecting a
				// transaction
				if (
					ADT.match(prev, "RefundTransaction") &&
					ADT.match(next, "SelectTransaction")
				) {
					return next;
				}

				// user confirming the transaction refund following selection or
				// finding an exact transaction
				if (
					ADT.match(prev, "RefundTransaction") &&
					ADT.match(next, "AwaitClientRefund")
				) {
					return next;
				}

				// user selected a transaction from a list of transactions to
				// refund and amount is not adjustable so proceed to automatic refund
				if (
					!amountAdjustable &&
					ADT.match(prev, "SelectTransaction") &&
					ADT.match(next, "AwaitClientRefund")
				) {
					return next;
				}

				// user is retrying refund from a response state
				if (
					ADT.match(prev, ResponseStates) &&
					ADT.match(next, "AwaitTransaction")
				) {
					return next;
				}

				// NOTE: if we are in a response state persist as requires user action
				return prev;
			});
		},
		[amountAdjustable, setState],
	);

	const sendClientRefundRequest = useCallback(
		({
			refundAmount,
			isoProcessingCode,
			transactionUuid,
		}: {
			refundAmount: number;
			isoProcessingCode: string;
			transactionUuid: string;
		}) => {
			/* v8 ignore start -- clientRef.current is assigned synchronously
			   immediately after the client is constructed and before the async
			   socket "open" event that invokes this callback fires, so the guard is
			   defensive and not reachable through the test harness. */
			if (!clientRef.current) {
				nextState(
					State.ErrorOperation({ onClose: closeWithConnectionFailure }),
				);
				return;
			}
			/* v8 ignore stop */

			sendRefundRequest({
				amount: refundAmount,
				client: clientRef.current,
				isoProcessingCode,
				purchase: transactionUuid,
				reference: reference ?? "",
				sessionUuid: clientSessionUuid,
				terminalContact,
				terminalNote,
				terminalReceipt,
				terminalReceiptPrint,
			});
		},
		[
			clientSessionUuid,
			closeWithConnectionFailure,
			nextState,
			reference,
			terminalContact,
			terminalNote,
			terminalReceipt,
			terminalReceiptPrint,
		],
	);

	const setInitialState = useCallback(() => {
		nextState(State.AwaitTransaction());
	}, [nextState]);

	const setTransactionErrorState = useCallback(
		async (
			transactionResponse: Awaited<ReturnType<typeof getTransactions>> | Error,
		) => {
			if (
				(transactionResponse instanceof Error &&
					transactionResponse.message.includes("Transaction not found")) ||
				("transactions" in transactionResponse &&
					transactionResponse.transactions.length === 0)
			) {
				nextState(
					State.ErrorTransactionNotFound({
						onClose: closeWithTransactionNotFound,
					}),
				);
				return;
			}

			const hasInternet = await checkInternetConnection();

			if (!hasInternet) {
				nextState(
					State.ErrorNetwork({
						onClose: closeWithNetworkFailure,
						onRetry: setInitialState,
					}),
				);
			} else if (!clientRef.current?.ready) {
				nextState(
					State.ErrorDisconnected({
						onClose: closeWithConnectionFailure,
						onRetry: setInitialState,
					}),
				);
			} else {
				nextState(
					State.ErrorOperation({ onClose: closeWithConnectionFailure }),
				);
			}
		},
		[
			closeWithConnectionFailure,
			closeWithNetworkFailure,
			closeWithTransactionNotFound,
			nextState,
			setInitialState,
		],
	);

	const setRefundTransactionState = useCallback(
		({
			transaction,
			isExternalMatch,
			onCancel = closeWithCancelled,
		}: {
			transaction: Exclude<Awaited<ReturnType<typeof getTransaction>>, Error>;
			isExternalMatch?: boolean;
			onCancel?: () => void;
		}) => {
			const transactionUuid = transaction.id;
			const isoProcessingCode = transaction.isoProcessingCode ?? "";

			if (amountAdjustable) {
				return nextState(
					State.RefundTransaction({
						onCancel,
						onRefund: (refundAmount: number) =>
							nextState(
								State.AwaitClientRefund({
									refundAmount,
									isoProcessingCode,
									transactionUuid,
								}),
							),
						refundAmount: amount ?? 0,
						transaction,
						isExternalMatch,
					}),
				);
			}

			nextState(
				State.AwaitClientRefund({
					refundAmount: amount,
					isoProcessingCode,
					transactionUuid,
				}),
			);
		},
		[amount, amountAdjustable, closeWithCancelled, nextState],
	);

	const onInactivityTimeout = useCallback(() => {
		setState((prev) => {
			if (prev instanceof Error) {
				return prev;
			}

			if (ADT.match(prev, "Initialising")) {
				return State.ErrorDisconnected({
					onClose: closeWithNotAvailable,
					onRetry: setInitialState,
				});
			}

			if (ADT.match(prev, TransactionStates)) {
				return State.ErrorInactivityTimeout({
					onClose: closeWithInactivityTimeout,
					onRetry: setInitialState,
				});
			}

			return prev;
		});
	}, [
		closeWithNotAvailable,
		closeWithInactivityTimeout,
		setInitialState,
		setState,
	]);

	const { clear: clearInactivityTimeout, start: startInactivityTimeout } =
		useInactivityTimeout(onInactivityTimeout, STATE_INITIAL_TIMEOUT);

	useEffect(() => {
		if (state instanceof Error) {
			clearInactivityTimeout();
			return;
		}

		// NOTE: if initialising start/restart inactivity timeout
		if (ADT.match(state, "Initialising")) {
			startInactivityTimeout();
			return;
		}

		// NOTE: if transaction state event return early
		if (ADT.match(state, TransactionStates)) {
			return;
		}

		// NOTE: if a response state event clear inactivity timeout
		clearInactivityTimeout();
	}, [clearInactivityTimeout, startInactivityTimeout, state]);

	useEffect(() => {
		return () => void clientRef.current?.close();
	}, []);

	useEffect(() => {
		if (state instanceof Error) {
			return;
		}

		if (ADT.match(state, "AwaitTransaction")) {
			const fetchTransactions = async (reference: string) => {
				try {
					const response = await getTransactions({
						apiClient,
						deviceId,
						purchaseId: purchase,
					});
					logger.debug("refund: fetching transactions", {
						reference,
						getTransactionsResponse: response,
					});

					setSelectTransactionState();

					function setSelectTransactionState() {
						if (response instanceof Error || !response.transactions.length) {
							setTransactionErrorState(response);
							return;
						}

						const firstTransaction = response.transactions[0];

						if (firstTransaction && response.transactions.length === 1) {
							// if amount passed matches transaction amount proceed to refund
							// automagically
							if (firstTransaction.amount === amount) {
								return nextState(
									State.AwaitClientRefund({
										refundAmount: firstTransaction.amount,
										isoProcessingCode: firstTransaction.isoProcessingCode ?? "",
										transactionUuid: firstTransaction.id,
									}),
								);
							}

							setRefundTransactionState({
								transaction: firstTransaction,
								isExternalMatch: false,
								onCancel: closeWithCancelled,
							});

							return;
						}

						nextState(
							State.SelectTransaction({
								amount,
								amountAdjustable,
								onCancel: closeWithCancelled,
								onConfirm: (transactionId) => {
									const transaction = response.transactions.find(
										(txn) => txn.id === transactionId,
									);

									if (transaction) {
										setRefundTransactionState({
											transaction,
											isExternalMatch: true,
											onCancel: setSelectTransactionState,
										});
									}
								},
								transactions: response.transactions,
							}),
						);
					}
				} catch (error) {
					if (error instanceof Error) {
						logger.error("refund: fetch transactions error", {
							errorName: error.name,
							errorMessage: error.message,
							errorStack: error.stack,
						});
					}

					setTransactionErrorState(new Error("Fetch Transactions Error"));
				}
			};

			const fetchTransaction = async () => {
				try {
					const response = await getTransaction({
						apiClient,
						deviceId,
						transactionId: purchase,
					});
					logger.debug("refund: fetching transaction", {
						getTransactionResponse: response,
					});
					if (response instanceof Error) {
						if (!reference) {
							setTransactionErrorState(response);
							return;
						}

						fetchTransactions(reference);
						return;
					}

					// if amount passed matches transaction amount proceed to refund
					// automagically
					if (response.amount === amount) {
						return nextState(
							State.AwaitClientRefund({
								refundAmount: response.amount,
								isoProcessingCode: response.isoProcessingCode ?? "",
								transactionUuid: response.id,
							}),
						);
					}

					setRefundTransactionState({ transaction: response });
				} catch (error) {
					if (error instanceof Error) {
						logger.error("refund: fetch transaction error", {
							errorName: error.name,
							errorMessage: error.message,
							errorStack: error.stack,
						});
					}

					setTransactionErrorState(new Error("Fetch Transactions Error"));
				}
			};

			if (!purchase) {
				nextState(
					State.ErrorTransactionNotFound({
						onClose: closeWithTransactionNotFound,
					}),
				);
				return;
			}

			// NOTE: When `purchase` is not a valid UUID, we know the getTransaction
			// call will fail, so skip to calling fetchTransactions with the purchase
			if (!isUuid(purchase)) {
				fetchTransactions(purchase);
				return;
			}

			fetchTransaction();
		}
	}, [
		amount,
		amountAdjustable,
		apiClient,
		closeWithCancelled,
		closeWithTransactionNotFound,
		deviceId,
		nextState,
		purchase,
		reference,
		setRefundTransactionState,
		setTransactionErrorState,
		state,
	]);

	const [authToken, setAuthToken] = useState<string>();
	useEffect(() => {
		if (!deviceId) {
			return void nextState(
				State.ErrorOperation({
					onClose: () =>
						void resolve.Error("Setup Required", {
							message: "Not configured.",
						}),
				}),
			);
		}

		if (!authToken) {
			tokens().then((tokens) => {
				if (tokens) {
					setAuthToken(tokens.authAccessToken);
				} else {
					nextState(
						State.ErrorOperation({
							onClose: () =>
								void resolve.Error("Setup Required", {
									message: "Not authenticated.",
								}),
						}),
					);
				}
			});
			return;
		}
		if (state instanceof Error) {
			return;
		}

		if (ADT.match(state, "Initialising")) {
			setInitialState();
		}
		if (ADT.match(state, "AwaitClientRefund")) {
			tokens().then((tokens) => {
				if (tokens) {
					const client = POSConnectorClient({
						authToken: tokens.authAccessToken,
						deviceUuid: deviceId,
						onOpen: () => {
							sendClientRefundRequest({ ...state });
						},
						onMessage: (message) => {
							if (message instanceof Error) {
								logger.error("refund: web socket on message error", {
									errorName: message.name,
									errorMessage: message.message,
									errorStack: message.stack,
								});

								nextState(new Error(message.message));
								return;
							}

							if (message.error) {
								return void (() => {
									logger.error("refund: web socket on message message error", {
										message: message.error.message,
										type: message.error.type,
									});

									if (
										message.error.type === "PAIRED_DEVICE_UNREACHABLE" ||
										// NOTE: remove when no longer supported
										(message.error.type === "FORBIDDEN" &&
											message.error.message ===
												"no connections found for paired devices")
									) {
										nextState(
											State.ErrorFailedToConnectToTerminal({
												onClose: closeWithConnectionFailure,
												onRetry: setInitialState,
											}),
										);
									} else {
										nextState(
											State.ErrorOperation({
												onClose: closeWithConnectionFailure,
											}),
										);
									}
								})();
							}

							// next schemas
							const acknowledgeEvent = unwrap(
								safe(() => RefundAcknowledgedSchema.parse(message)),
							);
							if (acknowledgeEvent) {
								return void (() => {
									logger.debug("refund: acknowledge", {
										state: acknowledgeEvent.data.state,
									});

									// NOTE: restart the inactivity timeout
									startInactivityTimeout(STATE_TRANSACTION_STATE_TIMEOUT);
								})();
							}

							const signatureVerificationEvent = unwrap(
								safe(() => RefundSignatureVerificationSchema.parse(message)),
							);
							if (signatureVerificationEvent) {
								return void (() => {
									const { data } = signatureVerificationEvent;

									// NOTE: restart the inactivity timeout
									startInactivityTimeout(STATE_TRANSACTION_STATE_TIMEOUT);

									logger.debug("refund: signature verification", {
										state: signatureVerificationEvent.data.state,
									});

									resolve.Event(data.state, {
										message: data.signature,
									});

									switch (data.state) {
										case "AWAITING_SIGNATURE":
										case "SIGNATURE_CLEARED":
											return nextState(
												State.AwaitSignature({ onClose: closeWithCancelled }),
											);
										case "SIGNATURE_SUBMITTED":
											return nextState(
												State.SignatureSubmitted({
													onClose: closeWithCancelled,
													onApprove: () => sendSignatureVerificationEvent(true),
													onDecline: () =>
														sendSignatureVerificationEvent(false),
												}),
											);
										default:
											logger.debug(
												"refund: unhandled signature verification event",
												{
													data: signatureVerificationEvent.data,
												},
											);
											/* v8 ignore next 1 -- exhaustiveness guard;
											   RefundSignatureVerificationSchema only parses a
											   closed union of states, so a value reaching this
											   default cannot be produced through the harness. */
											return never(data.state);
									}
								})();
							}

							const terminalBusyEvent = unwrap(
								safe(() => RefundTerminalBusySchema.parse(message)),
							);
							if (terminalBusyEvent) {
								return void (() => {
									logger.debug("refund: terminal busy", {
										status: terminalBusyEvent.data.status,
									});

									nextState(
										State.ErrorTerminalBusy({
											onClose: closeWithPairedDeviceBusy,
											onRetry: retryRefund,
										}),
									);
								})();
							}

							const transactionEvent = unwrap(
								safe(() => RefundTransactionEventSchema.parse(message)),
							);
							if (transactionEvent) {
								return void (() => {
									logger.debug("refund: transaction event", {
										data: transactionEvent.data,
									});

									const { data } = transactionEvent;

									// NOTE: restart the inactivity timeout
									startInactivityTimeout(STATE_TRANSACTION_STATE_TIMEOUT);

									logger.debug("refund: transaction event", {
										event: transactionEvent.data.event,
									});

									resolve.Event(data.event, {
										message: data.displayMessage,
									});

									switch (data.event) {
										case "AWAITING_ACCOUNT_SELECTION":
											return nextState(
												State.AwaitAccountSelection({
													onClose: closeWithCancelled,
												}),
											);
										case "AWAITING_APPLICATION_SELECTION":
											return nextState(
												State.AwaitApplicationSelection({
													onClose: closeWithCancelled,
												}),
											);
										case "AWAITING_PIN_ENTRY":
											return nextState(
												State.AwaitPinEntry({ onClose: closeWithCancelled }),
											);
										case "PROCESSING":
											return nextState(State.AwaitProcessing());
										case "DISPLAYING_PAYMENT_MESSAGE":
										case "RETRYING_PAYMENT_METHOD":
											return nextState(
												State.DisplayPaymentMessage({
													onClose: closeWithCancelled,
												}),
											);
										default:
											logger.debug("refund: unhandled transaction event", {
												data: transactionEvent.data,
											});
											/* v8 ignore next 1 -- exhaustiveness guard;
											   RefundTransactionEventSchema only parses a closed
											   union of events, so a value reaching this default
											   cannot be produced through the harness. */
											return never(data.event);
									}
								})();
							}

							const responseEvent = unwrap(
								safe(() => RefundResponseSchema.parse(message)),
							);
							if (responseEvent) {
								return void (() => {
									sendResponseAcknowledgedEvent();
									const { data } = responseEvent;

									logger.debug("refund: response", {
										status: responseEvent.data.status,
									});

									if (data.status === "APPROVED") {
										return (() => {
											const response = resolve.$Ok({
												...ProtocolTransaction_fromPosConnectorResponse(data),
												$type: "Approved",
												externalReference: reference,
												type: "REFUND",
											});

											return nextState(
												State.Approved({
													currency: data.currency,
													totalCents: data.amount,
													onClose: () => void resolve(response),
												}),
											);
										})();
									} else {
										const response =
											DeclineResponse_fromRefundPosConnectorResponse({
												data,
												reference,
											});
										return void nextState(
											State.Declined({
												...getPageProps({ data }),
												onCancel: () => resolve(response),
												onRetry: retryRefund,
											}),
										);
									}
								})();
							}
						},
						onClose: (event) => {
							// codes - https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code

							// normal closure
							if (event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE) {
								return;
							}

							// failed or abnormal closures
							logger.error("refund: web socket on close", {
								clientReady: client.ready,
								code: event.code,
								reason: event.reason,
								wasClean: event.wasClean,
							});
						},
						/* v8 ignore start -- socket "error" event is not emittable by the
						   MSW WebSocket harness (only open/close/message are), so this
						   handler cannot be driven from a browser test. The equivalent
						   network/disconnected/operation branches are exercised via
						   setTransactionErrorState. */
						onError: async (event) => {
							logger.error("refund: web socket on error", {
								clientReady: client.ready,
								event,
							});

							const hasInternet = await checkInternetConnection();

							if (!hasInternet) {
								nextState(
									State.ErrorNetwork({
										onClose: closeWithNetworkFailure,
										onRetry: setInitialState,
									}),
								);
							} else if (!client.ready) {
								nextState(
									State.ErrorDisconnected({
										onClose: closeWithConnectionFailure,
										onRetry: setInitialState,
									}),
								);
							} else {
								nextState(
									State.ErrorOperation({ onClose: closeWithConnectionFailure }),
								);
							}
						},
						/* v8 ignore stop */
					});

					clientRef.current = client;
				} else {
					nextState(
						State.ErrorOperation({
							onClose: () =>
								void resolve.Error("Setup Required", {
									message: "Not authenticated.",
								}),
						}),
					);
				}
			});
		}
	}, [
		authToken,
		closeWithCancelled,
		closeWithConnectionFailure,
		closeWithNetworkFailure,
		closeWithPairedDeviceBusy,
		deviceId,
		nextState,
		reference,
		retryRefund,
		sendClientRefundRequest,
		sendResponseAcknowledgedEvent,
		sendSignatureVerificationEvent,
		setInitialState,
		startInactivityTimeout,
		state,
		tokens,
	]);

	return state;
}

function sendRefundRequest({
	amount,
	client,
	isoProcessingCode,
	purchase,
	reference,
	sessionUuid,
	terminalContact,
	terminalNote,
	terminalReceipt,
	terminalReceiptPrint,
}: {
	amount: number;
	client: POSConnectorClient;
	isoProcessingCode: string;
	purchase: string;
	reference: string;
	sessionUuid: string;
	terminalContact?: boolean;
	terminalNote?: boolean;
	terminalReceipt?: boolean;
	terminalReceiptPrint?: boolean;
}) {
	client.send({
		eventId: uuidv4(),
		action: "refund",
		timestamp: Date.now().toString(),
		data: {
			amount,
			externalReference: reference,
			originalIsoProcessingCode: isoProcessingCode,
			originalTransactionUuid: purchase,
			sessionUuid: sessionUuid,
			terminalContact,
			terminalNote,
			terminalReceipt,
			terminalReceiptPrint,
			transactionUuid: uuidv4(),
		},
	});
}
